import React, { useEffect, useState } from 'react';
import { Form, Modal, Tab, Tabs } from 'react-bootstrap';
import type { MultiValue } from 'react-select';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateClientDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import { usePokCore } from '../../../common/hooks/usePokCore';
import CountrySelector from '../Selects/CountrySelector';
import { Option } from '../Selects/Selector';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import CompanySelector from '../Selects/CompanySelector';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { EveryClient } from './EveryClient';
import { ClientEditorControls } from './ClientEditorControls';
import { ClientVindication } from './ClientVindication';
import { ClientAdditional } from './ClientAdditional';

interface ClientEditorProps {
  id?: string;
  systemNumber?: number;
  client: CreateUpdateClientDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    client: CreateUpdateClientDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ClientEditor: React.FC<ClientEditorProps> = props => {
  const [client, setClient] = useState(props.client);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const pok = usePokCore();
  const { t, tk } = useTranslation('clientLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setClient(props.client);
    }
  }, [props.client, props.show]);

  const propertyChange = (obj: Partial<CreateUpdateClientDto>) => {
    setClient({ ...client, ...obj });
  };

  const findByNip = async (nip?: string) => {
    if (!nip) {
      setSaveErrors([t(tk.client.enterNIP)]);
      setShowSaveErrors(true);
      return;
    }
    try {
      const nipClient = await pok.clients.getByNip(nip.trim());
      if (nipClient) {
        setShowSaveErrors(false);
        setClient({
          ...client,
          name: nipClient.name || '',
          postcode: nipClient.postcode || '',
          place: nipClient.place || '',
          street: nipClient.street || '',
          numbers: nipClient.numbers || '',
          flat: nipClient.flat || '',
          shortname: '',
        });
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setSaveErrors(errors);
      setShowSaveErrors(true);
    }
  };

  const handleCompanyChange = (option: Option | MultiValue<Option>) => {
    const companyId = option ? (option as Option).value : undefined;
    propertyChange({
      companyId,
      isRelated: !!companyId,
    });
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? `${t(tk.client.clientEdit)} ${props.systemNumber}`
              : t(tk.client.clientAdd)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs>
            <Tab
              eventKey="client"
              title={t(tk.client.basicData)}
              className="m-3"
            >
              <Form className="d-grid gap-4 mb-3">
                <FormRow controlId="country" label={t(tk.client.country)}>
                  <CountrySelector
                    value={client?.countryId}
                    onChange={data =>
                      propertyChange({
                        countryId: data.countryId,
                        countryIsMain: data.isMain,
                      })
                    }
                  />
                </FormRow>
                <FormRow controlId="company" label={t(tk.common.company)}>
                  <CompanySelector
                    clearable={true}
                    loadData={() =>
                      pok.companies.getAllNotAssignedAndCurrent(
                        props.client.companyId,
                      )
                    }
                    value={client.companyId}
                    onChange={handleCompanyChange}
                  />
                </FormRow>
              </Form>
              <EveryClient
                client={client}
                propertyChange={propertyChange}
                findByNip={findByNip}
              />
            </Tab>
            <Tab
              eventKey="additional"
              title={t(tk.client.additionalParams)}
              className="m-3"
            >
              <ClientAdditional
                client={client}
                propertyChange={propertyChange}
              />
            </Tab>
            <Tab
              eventKey="vindication"
              title={t(tk.client.automaticVindication)}
              className="m-3"
            >
              <ClientVindication
                client={client}
                propertyChange={propertyChange}
              />
            </Tab>
          </Tabs>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <ClientEditorControls
            {...props}
            client={client}
            setSaveErrors={setSaveErrors}
            setShowSaveErrors={setShowSaveErrors}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
